import React from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";

function SectionFrame({
  sectionId,
  activeSection,
  studioMode,
  onSelectSection,
  onOpenSegmentTool,
  children,
  sx,
}) {
  const active = activeSection === sectionId;
  const select = () => onSelectSection?.(sectionId);
  const openTool = (event, tool) => {
    event.stopPropagation();
    onOpenSegmentTool?.(sectionId, tool);
  };

  return (
    <Box
      component="section"
      data-landing-section={sectionId}
      onClick={select}
      sx={{
        position: "relative",
        cursor: studioMode ? "pointer" : "default",
        outline: active ? "3px solid rgba(243,90,58,.76)" : "2px solid transparent",
        outlineOffset: -2,
        transition: "outline-color .15s ease",
        "&:hover": studioMode ? { outlineColor: "rgba(243,90,58,.42)" } : undefined,
        ...sx,
      }}
    >
      {studioMode && active ? (
        <Stack
          direction="row"
          spacing={0.4}
          sx={{
            position: "absolute",
            zIndex: 20,
            top: 8,
            right: 8,
            p: 0.45,
            borderRadius: 999,
            bgcolor: "rgba(25,36,47,.9)",
            boxShadow: "0 8px 20px rgba(0,0,0,.18)",
          }}
        >
          <IconButton size="small" aria-label="Editar textos" onClick={(event) => openTool(event, "content")} sx={{ color: "#fff" }}>
            <EditRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton size="small" aria-label="Editar colores" onClick={(event) => openTool(event, "colors")} sx={{ color: "#fff" }}>
            <PaletteRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton size="small" aria-label="Editar imágenes" onClick={(event) => openTool(event, "image")} sx={{ color: "#fff" }}>
            <ImageRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Stack>
      ) : null}
      {children}
    </Box>
  );
}

function PlaceholderCards({ labels, primary, surface }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.2, mt: 2 }}>
      {labels.map((label, index) => (
        <Paper key={`${label}-${index}`} elevation={0} sx={{ p: 1.5, borderRadius: 2.4, bgcolor: surface, border: "1px solid rgba(25,36,47,.08)" }}>
          <Box sx={{ height: 74, borderRadius: 1.8, bgcolor: `${primary}18`, mb: 1.2 }} />
          <Typography sx={{ fontSize: ".88rem", fontWeight: 900 }}>{label}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)" }}>Contenido automático del proyecto</Typography>
        </Paper>
      ))}
    </Box>
  );
}

export default function LandingCanvas({
  form = {},
  activeSection = "",
  device = "desktop",
  productsEnabled = true,
  onSelectSection,
  studioMode = false,
  onOpenSegmentTool,
}) {
  const business = form.business || {};
  const navigation = form.navigation || {};
  const hero = form.hero || {};
  const catalog = form.catalog || {};
  const about = form.about || {};
  const highlightsIntro = form.highlightsIntro || {};
  const highlights = Array.isArray(form.highlights) ? form.highlights : [];
  const contact = form.contact || {};
  const footer = form.footer || {};
  const sections = form.sections || {};
  const theme = form.theme || {};

  const primary = theme.primaryColor || "#F35A3A";
  const secondary = theme.secondaryColor || "#19242F";
  const surface = theme.surfaceColor || "#FFFFFF";
  const background = theme.backgroundColor || "#EEF0F5";
  const text = theme.textColor || "#1A1A2E";
  const muted = theme.mutedTextColor || "#5B5D73";
  const heroImage = resolveLandingAssetUrl(hero.slides?.[0]?.url || hero.imageUrl);
  const logo = resolveLandingAssetUrl(theme.logoUrl || theme.compactLogoUrl);
  const aboutImage = resolveLandingAssetUrl(about.imageUrl);
  const contactImage = resolveLandingAssetUrl(contact.backgroundImageUrl);
  const width = device === "mobile" ? 390 : 1180;

  return (
    <Box
      sx={{
        width: `min(100%, ${width}px)`,
        minHeight: "100%",
        bgcolor: background,
        color: text,
        overflow: "hidden",
        boxShadow: device === "mobile" ? "0 24px 65px rgba(25,36,47,.24)" : "none",
        borderRadius: device === "mobile" ? 4 : 0,
        fontFamily: theme.bodyFontFamily || theme.fontFamily || "Asap, sans-serif",
      }}
    >
      {sections.showNavbar !== false ? (
        <SectionFrame sectionId="navbar" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: { xs: 2, md: 4 }, py: 1.4, bgcolor: surface }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              {logo ? <Box component="img" src={logo} alt="" sx={{ width: 38, height: 38, objectFit: "contain" }} /> : null}
              <Typography noWrap sx={{ fontWeight: 950, color: secondary }}>{business.name || "Mi negocio"}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ display: { xs: "none", sm: "flex" } }}>
              <Typography variant="body2">{navigation.homeLabel || "Inicio"}</Typography>
              {navigation.showAboutLink !== false ? <Typography variant="body2">{navigation.aboutLabel || "Nosotros"}</Typography> : null}
              {productsEnabled && navigation.showProductsLink !== false ? <Typography variant="body2">{navigation.productsLabel || "Productos"}</Typography> : null}
              {navigation.showContactLink !== false ? <Typography variant="body2">{navigation.contactLabel || "Contacto"}</Typography> : null}
            </Stack>
          </Stack>
        </SectionFrame>
      ) : null}

      {sections.showHero !== false ? (
        <SectionFrame
          sectionId="hero"
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onOpenSegmentTool={onOpenSegmentTool}
          sx={{
            minHeight: device === "mobile" ? 430 : 520,
            display: "grid",
            alignItems: "center",
            backgroundImage: heroImage ? `${hero.overlay || "linear-gradient(rgba(0,0,0,.35),rgba(0,0,0,.55))"}, url(${heroImage})` : `linear-gradient(135deg, ${secondary}, ${primary})`,
            backgroundSize: "cover",
            backgroundPosition: hero.imagePosition || "center",
          }}
        >
          <Box sx={{ px: { xs: 2.3, sm: 5, md: 8 }, py: 7, maxWidth: 780, color: "#fff" }}>
            {hero.eyebrow ? <Typography variant="overline" sx={{ opacity: .78, fontWeight: 900 }}>{hero.eyebrow}</Typography> : null}
            <Typography sx={{ mt: .4, fontFamily: theme.headingFontFamily || "inherit", fontSize: { xs: "2.15rem", md: "3.75rem" }, lineHeight: 1.02, fontWeight: 1000, overflowWrap: "anywhere" }}>
              {hero.title || business.name || "Tu negocio, presentado con claridad"}
            </Typography>
            {hero.subtitle ? <Typography sx={{ mt: 1.5, maxWidth: 650, fontSize: { xs: ".98rem", md: "1.12rem" }, lineHeight: 1.65, opacity: .88 }}>{hero.subtitle}</Typography> : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.4, alignItems: { xs: "stretch", sm: "center" } }}>
              {hero.primaryCtaLabel ? <Button variant="contained" sx={{ bgcolor: primary, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>{hero.primaryCtaLabel}</Button> : null}
              {productsEnabled && hero.secondaryCtaLabel ? <Button variant="outlined" sx={{ color: "#fff", borderColor: "rgba(255,255,255,.65)", borderRadius: 999, textTransform: "none", fontWeight: 850 }}>{hero.secondaryCtaLabel}</Button> : null}
            </Stack>
          </Box>
        </SectionFrame>
      ) : null}

      {productsEnabled && sections.showSpotlight !== false && catalog.spotlight?.enabled ? (
        <SectionFrame sectionId="spotlight" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ p: { xs: 2, md: 4 }, bgcolor: catalog.spotlight.primaryColor || primary, color: "#fff" }}>
          <Typography variant="overline" sx={{ opacity: .75 }}>{catalog.spotlight.eyebrow}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 950 }}>{catalog.spotlight.title}</Typography>
          <Typography sx={{ mt: .8, opacity: .82 }}>{catalog.spotlight.description}</Typography>
        </SectionFrame>
      ) : null}

      {productsEnabled && (sections.showProducts !== false || sections.showFeatured !== false) ? (
        <SectionFrame sectionId="featured" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ px: { xs: 2, md: 5 }, py: { xs: 4, md: 6 }, bgcolor: background }}>
          <Typography variant="overline" sx={{ color: primary, fontWeight: 900 }}>{catalog.featured?.eyebrow || "Catálogo"}</Typography>
          <Typography variant="h4" sx={{ color: secondary, fontWeight: 950 }}>{catalog.featured?.title || "Productos y servicios"}</Typography>
          <Typography sx={{ mt: .6, color: muted }}>{catalog.featured?.subtitle}</Typography>
          <PlaceholderCards labels={["Producto o servicio 1", "Producto o servicio 2", "Producto o servicio 3"]} primary={primary} surface={surface} />
        </SectionFrame>
      ) : null}

      {productsEnabled && sections.showCategories !== false ? (
        <SectionFrame sectionId="categories" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ px: { xs: 2, md: 5 }, py: 4, bgcolor: surface }}>
          <Typography variant="h5" sx={{ color: secondary, fontWeight: 950 }}>{catalog.categories?.title || "Explorá por categorías"}</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {["Categoría 1", "Categoría 2", "Categoría 3"].map((item) => <Chip key={item} label={item} sx={{ fontWeight: 800 }} />)}
          </Stack>
        </SectionFrame>
      ) : null}

      {sections.showAbout !== false ? (
        <SectionFrame sectionId="about" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: about.layout === "image-right" ? "1.1fr .9fr" : ".9fr 1.1fr" }, bgcolor: form.appearance?.about?.backgroundColor || background }}>
          <Box sx={{ minHeight: { xs: 220, md: 390 }, order: about.layout === "image-right" ? { md: 2 } : undefined, bgcolor: `${primary}18`, backgroundImage: aboutImage ? `url(${aboutImage})` : "none", backgroundSize: "cover", backgroundPosition: about.imagePosition || "center" }} />
          <Box sx={{ p: { xs: 2.5, md: 6 }, alignSelf: "center" }}>
            <Typography variant="overline" sx={{ color: form.appearance?.about?.accentColor || primary, fontWeight: 900 }}>{about.eyebrow}</Typography>
            <Typography variant="h4" sx={{ color: form.appearance?.about?.titleColor || secondary, fontWeight: 950 }}>{about.title}</Typography>
            <Typography sx={{ mt: 1.2, color: form.appearance?.about?.textColor || muted, lineHeight: 1.7, whiteSpace: "pre-line" }}>{about.description}</Typography>
          </Box>
        </SectionFrame>
      ) : null}

      {sections.showHighlights !== false ? (
        <SectionFrame sectionId="highlights" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ px: { xs: 2, md: 5 }, py: { xs: 4, md: 6 }, bgcolor: form.appearance?.highlights?.backgroundColor || surface }}>
          <Typography variant="overline" sx={{ color: form.appearance?.highlights?.accentColor || primary, fontWeight: 900 }}>{highlightsIntro.eyebrow}</Typography>
          <Typography variant="h4" sx={{ color: form.appearance?.highlights?.titleColor || secondary, fontWeight: 950 }}>{highlightsIntro.title}</Typography>
          <Typography sx={{ mt: .7, color: form.appearance?.highlights?.textColor || muted }}>{highlightsIntro.subtitle}</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" }, gap: 1.2, mt: 2 }}>
            {highlights.slice(0, 3).map((item, index) => (
              <Paper key={item.id || index} elevation={0} sx={{ p: 2, borderRadius: 2.5, bgcolor: form.appearance?.highlights?.surfaceColor || background }}>
                <Typography sx={{ color: secondary, fontWeight: 950 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ mt: .6, color: muted, lineHeight: 1.55 }}>{item.description}</Typography>
              </Paper>
            ))}
          </Box>
        </SectionFrame>
      ) : null}

      {sections.showContact !== false ? (
        <SectionFrame sectionId="contact" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ px: { xs: 2, md: 5 }, py: { xs: 4, md: 6 }, color: "#fff", backgroundImage: contactImage ? `linear-gradient(rgba(25,36,47,.82),rgba(25,36,47,.82)),url(${contactImage})` : `linear-gradient(135deg, ${secondary}, #101820)`, backgroundSize: "cover" }}>
          <Typography variant="h4" sx={{ fontWeight: 950 }}>{contact.title || "Contacto"}</Typography>
          <Typography sx={{ mt: .8, opacity: .78 }}>{contact.subtitle || business.shortDescription}</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2 }}>
            {business.whatsapp ? <Button variant="contained" startIcon={<WhatsAppIcon />} sx={{ bgcolor: primary, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>{contact.whatsappCtaLabel || "Escribir por WhatsApp"}</Button> : null}
            {business.email ? <Chip label={business.email} sx={{ bgcolor: "rgba(255,255,255,.1)", color: "#fff" }} /> : null}
            {business.phone ? <Chip label={business.phone} sx={{ bgcolor: "rgba(255,255,255,.1)", color: "#fff" }} /> : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {sections.showFooter !== false ? (
        <SectionFrame sectionId="footer" activeSection={activeSection} studioMode={studioMode} onSelectSection={onSelectSection} onOpenSegmentTool={onOpenSegmentTool} sx={{ px: { xs: 2, md: 5 }, py: 3, bgcolor: secondary, color: "#fff" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
            <Typography sx={{ fontWeight: 900 }}>{business.name || "Mi negocio"}</Typography>
            <Typography variant="caption" sx={{ opacity: .65 }}>{String(footer.copyright || "{businessName}. Todos los derechos reservados.").replace("{businessName}", business.name || "Mi negocio")}</Typography>
          </Stack>
        </SectionFrame>
      ) : null}
    </Box>
  );
}
